/* =============================================================
   AccountOverview — a member's work on /account, app by app, for the
   apps they've added to their profile: invitations waiting, maps (own +
   shared with them), assessment results (view, delete) and draft, teams
   and recent checklists (GET /api/me/inventory). Members choose their
   apps (GET/PUT /api/me/apps); until they do, the profile shows only the
   apps they already have work in, so a new member starts with none.
   ============================================================ */

import { useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth';
import { APP_KEYS, appsWithWork, useDeleteResult, useInventory, useMyApps, useSetMyApps, type AppKey } from '../lib/account';
import { useIsAppAdmin } from '../lib/queries';
import { ASSESSMENT, CHECKLIST, MAP, WORKSPACES } from '../lib/routes';

const when = (d: string) => new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
const linkCls = 'font-label text-[11px] uppercase tracking-[0.15em] text-link hover:text-accent border-b border-accent-soft pb-0.5 whitespace-nowrap';

function Block({ title, count, action, headingId, children }: { title: string; count?: string; action?: ReactNode; headingId?: string; children: ReactNode }) {
  return (
    <section className="mb-12">
      <div className="flex items-baseline justify-between gap-4 flex-wrap border-b border-rule pb-3 mb-4">
        <h2 id={headingId} tabIndex={headingId ? -1 : undefined} className="font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent focus:outline-none">
          {title} {count && <span className="text-ink-muted font-normal normal-case tracking-normal">· {count}</span>}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Row({ title, meta, children }: { title: ReactNode; meta?: ReactNode; children?: ReactNode }) {
  return (
    <li className="py-3 border-b border-dotted border-rule flex flex-wrap items-baseline justify-between gap-x-5 gap-y-1">
      <div className="min-w-0 flex-1">
        <div className="font-editorial text-[16px] text-ink">{title}</div>
        {meta && <div className="font-editorial italic text-[13px] text-ink-muted">{meta}</div>}
      </div>
      {children && <div className="flex gap-4 items-baseline">{children}</div>}
    </li>
  );
}

const Empty = ({ children }: { children: ReactNode }) => (
  <p className="font-editorial italic text-[15px] text-ink-muted">{children}</p>
);

const APPS: Record<AppKey, { name: string; blurb: string }> = {
  map: { name: 'Interaction Map', blurb: 'Map the people and organizations in a system and how they interact. Save, share and publish maps.' },
  assessment: { name: 'Psychological Safety Assessment', blurb: 'A 143-item reflection on psychological safety at work, with your results and history.' },
  checklist: { name: 'CYBHI Onboarding Checklist', blurb: 'Plan CYBHI participation with your team: pathway, scenarios and every step.' },
};

export function AccountOverview() {
  const { user } = useAuth();
  const q = useInventory(user?.id);
  const appsQ = useMyApps(user?.id);
  const setApps = useSetMyApps(user?.id);
  const [picking, setPicking] = useState(false);
  /* Add and Remove rest briefly after each click: the block a click removes slides the next one's
     Remove under the pointer, and a double click mustn't take that app off too. */
  const [resting, setResting] = useState(false);
  const del = useDeleteResult();
  const [confirm, setConfirm] = useState<string | null>(null);
  /* The site's admin also sees the maps published with the site (the build's list), to edit them. */
  const isAdmin = useIsAppAdmin(user?.id).data === true;
  const siteMaps = useQuery<{ maps: { slug: string; title: string }[] }>({
    queryKey: ['site-maps'],
    enabled: isAdmin,
    queryFn: () => fetch('/interaction-map/p/site-maps.json', { cache: 'no-cache' }).then((r) => r.json()),
  });

  /* The admin's links never depend on the work list loading. */
  const adminBlock = isAdmin && (
    <Block title="Site admin" count="only you see this">
      <ul>
        <Row title="Weekly newsletter" meta="Prepare this week's issue and send it from your mailbox.">
          <Link to="/account/admin/newsletter" className={linkCls}>Open</Link>
        </Row>
        <Row title="Approval queue" meta="People waiting for access, and site numbers.">
          <Link to="/account/admin/approvals" className={linkCls}>Open</Link>
        </Row>
      </ul>
    </Block>
  );

  if (q.isLoading || appsQ.isLoading) return <Empty>Loading your work…</Empty>;
  if (q.error || !q.data)
    return (
      <>
        <div className="mb-12"><Empty>Your saved work couldn't be loaded just now. Refresh the page to try again.</Empty></div>
        {adminBlock}
      </>
    );
  const inv = q.data;
  const ownMaps = inv.maps.filter((m) => m.role === 'owner');
  const sharedMaps = inv.maps.filter((m) => m.role !== 'owner');
  /* The apps on this profile, in a fixed order, only names this page knows. If the choice can't be read,
     the apps with work are shown, but nothing can be added or removed: a save would replace the real choice. */
  const appsFailed = !!appsQ.error && !appsQ.data; // a failed re-read keeps the choice already loaded
  const chosen = (appsQ.data?.apps ?? appsWithWork(inv)).filter((k): k is AppKey => k in APPS);
  const has = (k: AppKey) => chosen.includes(k);
  const missing = APP_KEYS.filter((k) => !has(k));
  const busy = setApps.isPending || resting;
  /* The clicked button may disappear, so focus moves to the Your apps heading; a mouse click leaves the page
     where it is (event.detail > 0), a keyboard press scrolls to the heading. */
  const save = (apps: AppKey[], e: MouseEvent<HTMLButtonElement>) => {
    setResting(true);
    setApps.mutate(apps, { onSettled: () => window.setTimeout(() => setResting(false), 600) });
    document.getElementById('your-apps')?.focus({ preventScroll: e.detail > 0 });
  };
  const add = (k: AppKey, e: MouseEvent<HTMLButtonElement>) => save(APP_KEYS.filter((x) => x === k || has(x)), e);
  const remove = (k: AppKey, e: MouseEvent<HTMLButtonElement>) => save(chosen.filter((x) => x !== k), e);
  const removeBtn = (k: AppKey) =>
    appsFailed ? null : (
      <button type="button" onClick={(e) => remove(k, e)} disabled={busy} className={linkCls + ' disabled:opacity-40'} aria-label={`Remove ${APPS[k].name} from your profile (nothing is deleted)`}>
        Remove
      </button>
    );

  return (
    <div>
      <Block
        title="Your apps"
        headingId="your-apps"
        count={chosen.length ? `${chosen.length} of ${APP_KEYS.length}` : undefined}
        action={!appsFailed && chosen.length > 0 && missing.length > 0 ? (
          <button type="button" onClick={() => setPicking(!picking)} className={linkCls} aria-expanded={picking}>
            {picking ? 'Done' : 'Add an app'}
          </button>
        ) : undefined}
      >
        {chosen.length === 0 ? (
          <Empty>Add the apps you use. Each one shows here with your work in it.</Empty>
        ) : (
          <p className="font-editorial text-[15px] text-ink-soft">
            {chosen.map((k) => APPS[k].name).join(' · ')}.{' '}
            <span className="italic text-ink-muted">Removing an app only takes it off this page; your work in it stays saved.</span>
          </p>
        )}
        {appsFailed && (
          <p role="alert" className="font-editorial italic text-[14px] text-[#a02525] mt-2">
            {chosen.length
              ? "Your choice of apps couldn't be loaded, so these are the apps you have work in. Refresh the page to add or remove apps."
              : "Your choice of apps couldn't be loaded. Refresh the page to add apps."}
          </p>
        )}
        {!appsFailed && (chosen.length === 0 || picking) && missing.length > 0 && (
          <ul className="mt-2">
            {missing.map((k) => (
              <Row key={k} title={APPS[k].name} meta={APPS[k].blurb}>
                <button type="button" onClick={(e) => add(k, e)} className={linkCls + ' disabled:opacity-40'} disabled={busy} aria-label={`Add ${APPS[k].name} to my profile`}>
                  Add to my profile
                </button>
              </Row>
            ))}
          </ul>
        )}
        {setApps.error && <p role="alert" className="font-editorial italic text-[14px] text-[#a02525] mt-2">Your apps couldn't be saved. Try again.</p>}
      </Block>

      {inv.invites.length > 0 && (
        <Block title="Invitations waiting for you" count={String(inv.invites.length)}>
          <ul>
            {inv.invites.map((i) => (
              <Row key={i.token} title={i.org_name} meta={`Invited as ${i.role} · expires ${when(i.expires_at)}`}>
                <Link to={`/account/invite/${i.token}`} className={linkCls}>Review and join</Link>
              </Row>
            ))}
          </ul>
        </Block>
      )}

      {has('map') && <Block
        title="Interaction Map"
        count={`${plural(ownMaps.length, 'map')}${sharedMaps.length ? ` · ${sharedMaps.length} shared with you` : ''}`}
        action={<span className="flex gap-5"><a href={MAP} className={linkCls}>Open Interaction Map</a>{removeBtn('map')}</span>}
      >
        {inv.maps.length ? (
          <ul>
            {inv.maps.slice(0, 8).map((m) => (
              <Row
                key={m.id}
                title={m.title || 'Untitled map'}
                meta={`${m.site_slug ? 'On the site · ' : ''}${m.role === 'owner' ? (m.share_count ? `Shared with ${m.share_count === 1 ? '1 person' : `${m.share_count} people`} · ` : '') : `Shared by ${m.owner_name || m.owner_email} · ${m.role === 'edit' ? 'can edit' : 'view only'} · `}${plural(m.node_count, 'stakeholder')} · saved ${when(m.updated_at)}`}
              >
                <a href={`${MAP}?map=${m.id}`} className={linkCls}>Open</a>
                {m.site_slug && (
                  <>
                    {' '}
                    <a href={`/interaction-map/p/${m.site_slug}/`} className={linkCls}>View on site</a>
                  </>
                )}
              </Row>
            ))}
          </ul>
        ) : (
          <Empty>No saved maps yet.</Empty>
        )}
        {inv.maps.length > 8 && <p className="font-editorial italic text-[13px] text-ink-muted mt-3">All your maps are under File → My maps in the Interaction Map.</p>}
      </Block>}

      {isAdmin && !!siteMaps.data?.maps?.length && (
        <Block title="Site pages" count="Maps published with the site · only you see these">
          <ul>
            {siteMaps.data.maps.map((s) => {
              const copy = inv.maps.find((m) => m.role === 'owner' && m.site_slug === s.slug);
              return (
                <Row
                  key={s.slug}
                  title={s.title || s.slug}
                  meta={copy ? `Your working copy: ${copy.title || 'Untitled map'} · saved ${when(copy.updated_at)}` : 'No working copy yet'}
                >
                  <a href={copy ? `${MAP}?map=${copy.id}` : `/interaction-map/p/${s.slug}/?edit=1`} className={linkCls}>Edit</a>{' '}
                  <a href={`/interaction-map/p/${s.slug}/`} className={linkCls}>View</a>
                </Row>
              );
            })}
          </ul>
        </Block>
      )}

      {adminBlock}

      {has('assessment') && <Block
        title="Psychological Safety Assessment"
        count={plural(inv.results.length, 'result')}
        action={<span className="flex gap-5"><Link to={ASSESSMENT} className={linkCls}>{inv.draft ? 'Resume the assessment' : 'Open assessment'}</Link>{removeBtn('assessment')}</span>}
      >
        {inv.draft && <p className="font-editorial text-[15px] text-ink-soft mb-3">You have an assessment in progress, last saved {when(inv.draft.updated_at)}.</p>}
        {inv.results.length ? (
          <ul>
            {inv.results.map((r) => (
              <Row
                key={r.id}
                title={`Completed ${when(r.completed_at)}`}
                meta={`${r.overall_score != null ? `Composite ${Number(r.overall_score).toFixed(2)}/5` : 'No score'}${r.org_name ? ` · shared with ${r.org_name}` : ' · private'}`}
              >
                <Link to={`${ASSESSMENT}/results/${r.id}`} className={linkCls}>View</Link>
                {confirm === r.id ? (
                  <>
                    <button
                      type="button"
                      disabled={del.isPending}
                      onClick={() => del.mutate(r.id, { onSettled: () => setConfirm(null) })}
                      className="font-label text-[11px] uppercase tracking-[0.15em] text-[#a02525] border-b border-[#a02525] pb-0.5"
                    >
                      {del.isPending ? 'Deleting…' : 'Confirm delete'}
                    </button>
                    <button type="button" onClick={() => setConfirm(null)} className={linkCls}>Cancel</button>
                  </>
                ) : (
                  <button type="button" onClick={() => setConfirm(r.id)} className={linkCls}>Delete</button>
                )}
              </Row>
            ))}
          </ul>
        ) : (
          <Empty>No completed assessments yet.</Empty>
        )}
        {del.error && <p className="font-editorial italic text-[14px] text-[#a02525] mt-2">That result couldn't be deleted. Try again.</p>}
      </Block>}

      {has('checklist') && <Block
        title="Teams and CYBHI checklists"
        count={`${plural(inv.workspaces.length, 'team')} · ${plural(inv.checklists.length, 'checklist')}`}
        action={<span className="flex gap-5"><Link to={WORKSPACES} className={linkCls}>Manage teams</Link>{removeBtn('checklist')}</span>}
      >
        {inv.workspaces.length ? (
          <ul>
            {inv.workspaces.map((w) => (
              <Row key={w.id} title={w.name} meta={`You're ${w.role === 'owner' ? 'the owner' : `an ${w.role}`.replace('an member', 'a member')} · ${plural(w.member_count, 'member')} · ${plural(w.checklist_count, 'checklist')}`}>
                <Link to={`${WORKSPACES}/${w.id}`} className={linkCls}>Open</Link>
              </Row>
            ))}
          </ul>
        ) : (
          <Empty>
            You're not in a team yet. Teams hold checklists and group assessment results.{' '}
            <Link to={`${WORKSPACES}/new`} className="text-link underline underline-offset-2 not-italic">Create one</Link> or{' '}
            <Link to={`${CHECKLIST}/select`} className="text-link underline underline-offset-2 not-italic">start a checklist</Link>.
          </Empty>
        )}
        {inv.checklists.length > 0 && (
          <>
            <h3 className="font-label text-[10px] uppercase tracking-[0.18em] text-ink-muted mt-6 mb-1">Recent checklists</h3>
            <ul>
              {inv.checklists.slice(0, 6).map((c) => (
                <Row key={c.id} title={c.name} meta={`${c.org_name}${c.pathway ? ` · Pathway ${c.pathway}` : ''} · updated ${when(c.updated_at)}`}>
                  <Link to={`${CHECKLIST}/team/${c.org_id}/checklist/${c.id}`} className={linkCls}>Open</Link>
                </Row>
              ))}
            </ul>
          </>
        )}
      </Block>}
    </div>
  );
}
